import React, { useState } from 'react';
import HouseMemoryArchive from './HouseMemoryArchive';
import { wallDayLabel, longDay, leafOf } from '../../utils/houseMemoryBook';

/**
 * ✂️→📖 ExpandableText (Nina 09-15, the "relying on 'pi" arc): memories are
 * no longer guillotined at storage (houseMemory.js keeps whole sentences up
 * to a 600-char runaway ceiling), so a long entry is REAL content. Short
 * entries render exactly as before; past the threshold the card clamps to
 * 3 lines and grows a "more" toggle — scannable wall, nothing hidden.
 * Threshold is chars (cheap, SSR-safe), the clamp is lines (what the eye
 * sees); both err toward showing full text.
 */
function ExpandableText({ text, style }) {
  const [open, setOpen] = useState(false);
  const s = String(text || '');
  const long = s.length > 200;
  return (
    <div style={style}>
      <span style={(long && !open) ? {
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      } : undefined}>{s}</span>
      {long && (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          style={{
            display: 'block', marginTop: 2, padding: 0, border: 'none',
            background: 'none', cursor: 'pointer', fontSize: '0.85em',
            fontWeight: 700, color: '#8a5f33', textDecoration: 'underline',
          }}
        >{open ? 'less' : 'more'}</button>
      )}
    </div>
  );
}

/**
 * 🏠🧠 The Memory Wall — the Claude House remembers, and shows it
 * (CLAUDE_HOUSE_AUTONOMY_BRAINSTORM §2 "visible memory": the DB as a
 * browsable feature — proof to regulars that their visits mattered).
 *
 * Three stacked surfaces on one corkboard:
 *   🗝️ OPEN MYSTERIES (10-14, R24 #6) — one pinned index card per house mystery, only
 *      when the lazy fetch carries `threads` (backend HOUSE_MYSTERY_CARD_ENABLED)
 *   💰 the house till crowdfund bar (progress toward the next /build)
 *   🏠 HOUSE TRUTHS — the canon store, framed like little plaques
 *   🧠 pinned memory cards — the shared house log, newest first
 *
 * Data contract: canon + till ride chatGameStatus.house (5s poll, cheap);
 * memories arrive via the lazy houseMemoryWall fetch the PAGE performs when
 * the wall opens — this component never fetches. SHARED memories only ever
 * reach the client (player_id IS NULL is enforced server-side); a resident's
 * private impressions are not a display surface, by invariant.
 *
 * Chrome stays in the page (drawer / bottom sheet / Locals section) — same
 * contract as SpaceShelf: this is just the board.
 */

const CORK_TEXT = '#3b2a1a';
const CORK_FAINT = 'rgba(59,42,26,0.75)';
const PAPER = '#fffdf4';

// Deterministic tilt per card (SpaceShelf.wobble pattern): hash the content
// so a card always leans the same way across poll-driven re-renders.
const tilt = (text) => {
  let h = 0;
  const s = String(text || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ((h % 5) - 2); // -2..2 deg
};

// 🕐 09-24: memory days are HOUSE days (Phoenix). This compared them to a UTC
// "today", so from 5pm house time every label was a day off. The server sends
// house_today; wallDayLabel falls back to the house zone itself.
const dayLabel = (day, today) => wallDayLabel(day, today);
// ⚠️ poll_history days are still minted in UTC (spacePolls.js closePoll), so
// they keep the UTC comparison until that writer moves to the house clock.
const pollDayLabel = (day) => wallDayLabel(day, new Date().toISOString().slice(0, 10));

// 📖 "Older memories" (09-24): the wall shows the newest days; the book holds
// every day. Hidden until the backend sends the archive summary.
const OLDER_BTN = {
  border: '1px solid #8a5f33', background: PAPER, color: CORK_TEXT, textShadow: 'none',
  borderRadius: 999, padding: '4px 10px', fontSize: '0.72em', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(0,0,0,0.18)', whiteSpace: 'nowrap',
};

export function TillBar({ till, goal, compact = false, onTip = null, tipState = null, patrons = null }) {
  const t = Math.max(0, Number(till) || 0);
  const g = Math.max(1, Number(goal) || 40);
  const pct = Math.min(100, Math.round((t / g) * 100));
  // 🪙 Chip in (§5.3 lane 1, Nina go 08-12): one tap = +1 to the till, no
  // text rides a tip. tipState: null=idle · 'busy' · 'done' · 'capped'.
  const tipDisabled = tipState === 'busy' || tipState === 'capped';
  const tipLabel = tipState === 'capped' ? 'Jar full today'
    : tipState === 'done' ? '+1 🪙 Thanks!'
    : tipState === 'busy' ? '…'
    : '🪙 Add a coin'; // 💰 10-02 (Nina): "Chip in" is the battery's money button
  const recentPatrons = Array.isArray(patrons) && patrons.length
    ? Object.entries(patrons.reduce((m, p) => { m[p.name] = (m[p.name] || 0) + 1; return m; }, {}))
      .map(([name, n]) => (n > 1 ? `${name} ×${n}` : name)).slice(-3).join(' · ')
    : null;
  return (
    <div style={{
      borderRadius: 10, padding: compact ? '8px 10px' : '10px 12px', textShadow: 'none',
      background: 'rgba(255,253,244,0.92)', border: '1px solid #c9a45e',
      boxShadow: '0 3px 8px rgba(0,0,0,0.22)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: compact ? '0.8em' : '0.88em', color: CORK_TEXT }}>💰 House till</span>
        <span style={{ fontWeight: 800, fontSize: compact ? '0.8em' : '0.88em', color: '#8a5f33' }}>{t} / {g} 🪙</span>
        {onTip && (
          <button
            type="button"
            onClick={() => { if (!tipDisabled) onTip(); }}
            disabled={tipDisabled}
            style={{
              marginLeft: 'auto', padding: '3px 10px', borderRadius: 999,
              border: '1px solid #b28f4a', cursor: tipDisabled ? 'default' : 'pointer',
              background: tipState === 'done' ? '#e8f5df' : 'linear-gradient(180deg, #f6e3b4, #ecd08c)',
              color: '#6b4a1e', fontWeight: 800, fontSize: '0.72em',
              opacity: tipState === 'capped' ? 0.6 : 1,
              transition: 'background 0.2s',
            }}
            title="Add a coin to the house till (3 a day)"
          >{tipLabel}</button>
        )}
      </div>
      <div style={{
        marginTop: 6, height: 10, borderRadius: 999, overflow: 'hidden',
        background: 'rgba(138,95,51,0.22)', border: '1px solid rgba(138,95,51,0.35)',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: 999,
          background: 'linear-gradient(90deg, #d9a94e, #f0c96a)',
          transition: 'width 0.5s ease',
        }} />
      </div>
      <div style={{ fontSize: '0.7em', color: CORK_FAINT, marginTop: 4 }}>
        {onTip
          ? 'Saving up for the next big /build. The locals chip in. So can you.'
          : 'Saving up for the next big /build. The locals chip in. So can their fans, someday.'}
      </div>
      {recentPatrons && (
        <div style={{ fontSize: '0.68em', color: '#8a5f33', marginTop: 3, fontWeight: 600 }}>
          recent patrons: {recentPatrons}
        </div>
      )}
    </div>
  );
}

// 🗝️ 10-14 the "Open mysteries" card (R24 #6; backend houseMysteryCard.js → houseThreads.publicThreads): what
// already happened + the next date, never what comes next. During a reveal it points at the poll (the wall is
// fetched once, so it never carries live vote buttons). The calendar leaf is DRAWN (📅 reads JUL 17 on a Mac).
const MYSTERY_RED = '#b5463c';
function MysteryCard({ t, today }) {
  const pips = (t && t.pips && Array.isArray(t.pips.labels)) ? t.pips : { labels: [], filled: 0 };
  const leaf = t.next && t.next.day ? leafOf(t.next.day) : null;
  return (
    <div data-mystery-card={t.id || '1'} style={{
      position: 'relative', margin: '0 4px 10px', padding: '10px 12px 9px', borderRadius: 4,
      transform: `rotate(${tilt(t.id || t.title) / 2}deg)`, background: PAPER,
      border: '1px solid rgba(178,143,74,0.55)', borderTop: `3px solid ${MYSTERY_RED}`,
      boxShadow: '0 3px 8px rgba(0,0,0,0.22)', color: CORK_TEXT,
      textShadow: 'none', // paper resets the halo itself (roomHaloPaper.test.js reads each component alone)
    }}>
      <span aria-hidden="true" style={{
        position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)',
        fontSize: 11, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
      }}>📍</span>
      <div style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: '0.98em', lineHeight: 1.25 }}>{t.title}</div>
      {t.holder && <div style={{ fontSize: '0.74em', fontStyle: 'italic', color: CORK_FAINT, marginTop: 2 }}>{t.holder}</div>}
      {Array.isArray(t.facts) && t.facts.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '7px 0 0', padding: 0 }}>
          {t.facts.map((f, i) => (
            <li key={i} style={{ fontSize: '0.75em', lineHeight: 1.38, margin: '0 0 4px', display: 'flex', gap: 6 }}>
              <span style={{ flex: '0 0 auto', color: CORK_FAINT, fontWeight: 700, minWidth: 44 }}>{dayLabel(f.day, today)}</span>
              <span style={{ minWidth: 0 }}>{f.text}</span>
            </li>
          ))}
        </ul>
      )}
      {pips.labels.length > 0 && (
        <div aria-label={`${pips.filled} of ${pips.labels.length}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 7 }}>
          {pips.labels.map((label, i) => (
            <span key={label} data-pip={i < pips.filled ? 'on' : 'off'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.66em', fontWeight: 700, color: i < pips.filled ? CORK_TEXT : CORK_FAINT }}>
              <span aria-hidden="true" style={{
                width: 8, height: 8, borderRadius: '50%', boxSizing: 'border-box',
                background: i < pips.filled ? MYSTERY_RED : 'transparent', border: `1.5px solid ${i < pips.filled ? MYSTERY_RED : 'rgba(59,42,26,0.45)'}`,
              }} />
              {label}
            </span>
          ))}
        </div>
      )}
      {t.voting ? (
        <div style={{ marginTop: 8, fontSize: '0.76em', fontWeight: 800 }}>🗳️ The house is voting. The poll is in the chat.</div>
      ) : leaf ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span aria-hidden="true" style={{
            display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: 30, borderRadius: 4, overflow: 'hidden',
            border: '1px solid rgba(59,42,26,0.35)', background: '#fff', lineHeight: 1,
          }}>
            <span style={{ width: '100%', textAlign: 'center', background: MYSTERY_RED, color: '#fff', fontSize: 8, fontWeight: 800, padding: '2px 0' }}>{leaf.mo}</span>
            <span style={{ fontSize: 13, fontWeight: 800, padding: '2px 4px 3px' }}>{leaf.dn}</span>
          </span>
          <span style={{ fontSize: '0.76em' }}>
            <b>Next: {t.next.label}</b>
            <span style={{ color: CORK_FAINT }}>{` · ${longDay(t.next.day)}`}</span>
          </span>
        </div>
      ) : null}
      {t.plaque && t.plaque.text && (
        <div style={{
          display: 'inline-block', marginTop: 8, padding: '3px 9px', borderRadius: 3, fontSize: '0.72em', fontWeight: 800,
          background: 'linear-gradient(180deg, #f6e3b4, #e2c27c)', border: '1px solid #b28f4a', color: '#5b3d16',
        }}>{t.plaque.text}{t.plaque.day ? ` · ${longDay(t.plaque.day)}` : ''}</div>
      )}
      {t.started_by_visitor && (
        <div style={{ fontSize: '0.64em', color: CORK_FAINT, marginTop: 6 }}>Started by a visitor.</div>
      )}
    </div>
  );
}

export default function HouseMemoryWall({ canon = [], memories = [], scenes = [], pollHistory = [], threads = [], loading = false, till = null, buildGoal = 40, onTip = null, tipState = null, patrons = null, gameId = null, houseToday = null, archive = null }) {
  const hasAnything = (canon && canon.length) || (memories && memories.length) || (scenes && scenes.length) || (pollHistory && pollHistory.length) || (threads && threads.length);
  const [bookFrom, setBookFrom] = useState(null); // the button that opened the book (focus returns there), null = closed
  const [bookOpen, setBookOpen] = useState(false);
  const canOpenBook = !!gameId && !!archive && Number(archive.days) > 0;
  const openBook = (e) => { setBookFrom(e.currentTarget); setBookOpen(true); };
  return (
    // 🗒️ PAPER RESETS THE HALO (09-24): the wall is mounted inside the chat
    // column, whose inherited dark text-shadow smears dark ink on these light
    // cards (roomHaloPaper.test.js). One reset at the root covers every card.
    <div style={{ padding: '4px 2px 10px', textShadow: 'none' }}>
      {/* 🗝️ 10-14 Open mysteries (R24 #6): above everything, absent unless the fetch carried threads */}
      {Array.isArray(threads) && threads.length > 0 && (
        <div data-wall-mysteries="1" style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: CORK_FAINT, margin: '2px 2px 8px',
          }}>🗝️ Open mysteries</div>
          {threads.filter((t) => t && t.title).map((t, i) => <MysteryCard key={t.id || i} t={t} today={houseToday} />)}
        </div>
      )}

      {till !== null && (
        <div style={{ marginBottom: 10 }}>
          <TillBar till={till} goal={buildGoal} onTip={onTip} tipState={tipState} patrons={patrons} />
        </div>
      )}

      {canon && canon.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: CORK_FAINT, margin: '2px 2px 6px',
          }}>🏠 House truths</div>
          {canon.map((line, i) => (
            <div key={i} style={{
              margin: '0 2px 6px', padding: '7px 10px', borderRadius: 8,
              background: 'rgba(255,248,230,0.95)', border: '1px solid #b28f4a',
              boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
              fontSize: '0.8em', fontWeight: 600, color: CORK_TEXT, lineHeight: 1.35,
            }}><ExpandableText text={line} /></div>
          ))}
        </div>
      )}

      {/* 🎬 Scenes (Nina 08-06): the house's image moments — dream art,
          comics, build renders — as polaroids taped to the board. */}
      {scenes && scenes.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: CORK_FAINT, margin: '2px 2px 6px',
          }}>🎬 Scenes</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, padding: '0 2px' }}>
            {scenes.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" style={{
                display: 'block', textDecoration: 'none', transform: `rotate(${tilt(s.url)}deg)`,
                background: PAPER, border: '1px solid rgba(178,143,74,0.55)', borderRadius: 4,
                padding: '5px 5px 6px', boxShadow: '0 3px 7px rgba(0,0,0,0.2)',
              }}>
                <img src={s.url} alt={s.caption || 'a house moment'} loading="lazy" style={{
                  width: '100%', aspectRatio: '16/10', objectFit: 'cover', borderRadius: 2, display: 'block',
                }} />
                {s.caption && (
                  <div style={{
                    fontSize: '0.62em', color: CORK_TEXT, lineHeight: 1.3, marginTop: 4,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>{s.caption}</div>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* 🗳️ House votes (Nina 08-06): closed-poll results, ballot slips. */}
      {pollHistory && pollHistory.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: CORK_FAINT, margin: '2px 2px 6px',
          }}>🗳️ House votes</div>
          {pollHistory.map((p, i) => (
            <div key={i} style={{
              margin: '0 2px 6px', padding: '6px 10px', borderRadius: 6,
              background: 'rgba(255,255,255,0.9)', border: '1px dashed rgba(178,143,74,0.7)',
              fontSize: '0.76em', color: CORK_TEXT, lineHeight: 1.35,
              display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline',
            }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ opacity: 0.75 }}>{p.question}</span>
                {' → '}<strong>{p.winner}</strong>
              </span>
              <span style={{ whiteSpace: 'nowrap', color: CORK_FAINT, fontSize: '0.9em' }}>
                {p.votes} vote{p.votes === 1 ? '' : 's'}{pollDayLabel(p.day) ? ` · ${pollDayLabel(p.day)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '2px 2px 6px' }}>
        <span style={{
          fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: CORK_FAINT,
        }}>🧠 The house remembers</span>
        {canOpenBook && (
          <button type="button" onClick={openBook} style={OLDER_BTN}>📖 Older memories</button>
        )}
      </div>

      {loading && (
        <div style={{ padding: '16px 10px', textAlign: 'center', color: CORK_FAINT, fontSize: '0.82em' }}>
          Checking the wall...
        </div>
      )}

      {!loading && !memories?.length && (
        <div style={{ padding: '14px 10px 18px', textAlign: 'center', color: CORK_TEXT }}>
          <div style={{ fontSize: '1.8em', marginBottom: 6 }}>📌</div>
          <div style={{ fontSize: '0.86em', fontWeight: 700 }}>Nothing pinned yet.</div>
          <div style={{ fontSize: '0.75em', marginTop: 5, color: CORK_FAINT }}>
            The house writes its diary every night. Come back tomorrow.
          </div>
        </div>
      )}

      {!loading && memories?.map((m, i) => {
        const rot = tilt(m.content);
        const when = dayLabel(m.day, houseToday);
        return (
          <div key={i} style={{
            position: 'relative', margin: '0 4px 9px', padding: '9px 11px 8px',
            borderRadius: 6, transform: `rotate(${rot}deg)`,
            background: PAPER, border: '1px solid rgba(178,143,74,0.55)',
            boxShadow: '0 3px 7px rgba(0,0,0,0.2)',
          }}>
            {/* pushpin */}
            <span aria-hidden="true" style={{
              position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)',
              fontSize: 11, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
            }}>📍</span>
            <ExpandableText text={m.content} style={{ fontSize: '0.8em', color: CORK_TEXT, lineHeight: 1.4 }} />
            {when && (
              <div style={{ fontSize: '0.66em', color: CORK_FAINT, marginTop: 4, textAlign: 'right' }}>{when}</div>
            )}
          </div>
        );
      })}

      {!loading && canOpenBook && memories?.length > 0 && (
        <button type="button" onClick={openBook} style={{
          display: 'block', width: 'calc(100% - 8px)', margin: '6px 4px 8px', padding: '12px 10px', cursor: 'pointer',
          border: '1px dashed #8a5f33', borderRadius: 8, background: '#f9f1df', color: CORK_TEXT, textShadow: 'none',
          fontSize: '0.82em', fontWeight: 800, textAlign: 'center',
        }}>
          📖 Older memories
          <span style={{ display: 'block', fontWeight: 600, color: CORK_FAINT, marginTop: 2, fontSize: '0.88em' }}>
            {Number(archive.days).toLocaleString('en-US')} days{archive.first_day ? ` since ${longDay(archive.first_day)}` : ''}
          </span>
        </button>
      )}

      {!loading && hasAnything && (
        <div style={{ fontSize: '0.68em', textAlign: 'center', color: CORK_FAINT, marginTop: 6 }}>
          Written nightly by the house. Visits make memories.
        </div>
      )}

      {bookOpen && canOpenBook && (
        <HouseMemoryArchive gameId={gameId} returnFocusTo={bookFrom} onClose={() => setBookOpen(false)} />
      )}
    </div>
  );
}
